<template>
  <div class="w-full min-w-[200px] relative">
    <!-- Select  -->
    <button
      :id="`itemsTreeSelector-${uid}`"
      :ref="`itemsTreeSelector-${uid}`"
      :disabled="disableFilter"
      class="flex items-center justify-between w-full bg-white px-3 py-2.5 leading-tight border border-[#0000001A] focus:shadow-outline select-none rounded-sm shadow-sm outline-none focus:border-main-primary focus:ring-main-primary disabled:bg-zinc-300 disabled:cursor-not-allowed disabled:pointer-events-none"
      @click.prevent="togglePanel"
    >
      <div class="flex flex-col items-start gap-1 font-normal text-sm">
        <span
          v-if="!selectedItems"
          class="flex items-center gap-x-1 text-theme-subtitle-grey"
        >
          Select {{ titleName }}
        </span>
        <span
          v-else
          class="flex items-center gap-x-1 text-theme-text-black"
        >
          {{ selectedItems }} {{ titleName }} Selected
        </span>
      </div>
      <div class="flex gap-2">
        <span class="text-gray-300">|</span>
        <ChevronDownSmall
          class="text-gray-500 duration-150 transform"
          :class="{ 'rotate-180': isOpen }"
        />
      </div>
    </button>
    <!-- Panel -->
    <div
      v-if="isOpen"
      :ref="`itemsTreePanel-${uid}`"
    >
      <section
        class="w-full absolute h-auto max-h-[600px] overflow-auto top-10 z-50 pb-4 bg-white border border-[#0000001A] rounded-e-md shadow-lg p-2 pt-0"
      >
        <header class="sticky left-0 pt-2 pb-3">
          <div class="flex justify-between text-md">
            <span class="text-main-primary">{{ titleName }}</span>
          </div>
          <InputWithIcon
            :val="itemQuery"
            classes="mt-1 text-md rounded-lg placeholder:text-md"
            placeholder="Search..."
            @input="itemQuery = $event"
          >
            <template #icon>
              <SearchIcon custom-style="size-2.5" />
            </template>
          </InputWithIcon>
        </header>
        <div
          v-for="(treegroup, tIndex) in itemsTree"
          :key="`treegroup-${tIndex}`"
          class="py-2"
        >
          <DataAssignmentNodeSelector
            :is-item-node="isAsset"
            :node="treegroup"
            @toggle-children="handleToggleProp($event, 'expandedChildren')"
            @toggle-check-item="handleCheckedItem"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'DataAssignmentSelector',

  props: {
    items: {
      type: Array,
      required: true,
    },
    resetAsFilter: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isAsset: {
      type: Boolean,
      default: true,
    },
    titleName: {
      type: String,
      default: '',
    },
    selectedItems: {
      type: Number,
      default: 0,
    },
  },

  emits: ['selector-updated'],

  data() {
    return {
      listOfItems: this.transformItems(this.items),
      isOpen: false,
      selectorTop: 0,
      selectorLeft: 0,
      selectorWidth: 0,
      itemQuery: '',
      uid: null,
    }
  },

  computed: {
    canReset() {
      return !this.isOpen && this.resetAsFilter && this.isFiltersApplied
    },
    isFiltersApplied() {
      const numberOfFiltersChecked = [...this.listOfItems].filter(({ checked }) => checked).length
      // If the number of filters checked is the same as the org's item, meaning everything is settled so technically no filter is applied
      return numberOfFiltersChecked !== this.listOfItems.length
    },
    disableFilter() {
      return this.disabled
    },
    itemsTree() {
      const map = new Map()
      const tree = []

      this.listOfItems.forEach((item) => {
        map.set(item.id, { ...item, children: [] })
      })

      this.listOfItems.forEach(({ id, parent_node }) => {
        if (parent_node !== null) {
          const parent = map.get(parent_node)
          if (parent) {
            parent.children.push(map.get(id))
          }
        }
        else {
          tree.push(map.get(id))
        }
      })
      return tree
    },
  },

  watch: {
    isOpen(val) {
      // This ensures that the DOM is updated and the selector is fully rendered before the event listener is added.
      nextTick(() => {
        if (val) {
          document.body.addEventListener('click', this.handleOutsideClick)
          document.body.addEventListener('keydown', this.handleEscapeKey)
          this.positionSelectorPanel()
          this.positionSelectorPanel = debounce(this.positionSelectorPanel, 50)
          document.addEventListener('scroll', this.positionSelectorPanel, true)
          window.addEventListener('resize', this.positionSelectorPanel, true)
        }
      })
    },
    itemQuery(newQuery) {
      // On item search
      this.deselectAllItems(true)

      if (newQuery) {
        this.listOfItems = [...this.listOfItems].map((item) => {
          if (!item.name.toLowerCase().includes(newQuery.toLowerCase())) {
            item.disabledSelection = true
          }
          else {
            item.disabledSelection = false
          }
          return item
        })
      }
    },
  },

  mounted() {
    this.uid = this.$.uid
  },

  beforeUnmount() {
    if (this.isOpen) this.killAllListeners()
  },

  methods: {
    handleToggleProp(itemId, key) {
      // Sync with the child component
      this.listOfItems = this.listOfItems.map((item) => {
        if (item.id === itemId) {
          item[key] = !item[key]
        }
        return item
      })
    },
    checkChildrenOfParentNode(parent, check = true) {
      const updatedItems = [...this.listOfItems]
      updatedItems.forEach((item) => {
        if (item.id === parent && item.checked !== check && !this.isAsset) {
          item.checked = check
        }
      })
      this.listOfItems = updatedItems
      this.listOfItems.filter(({ parent_node }) => parent_node === parent).forEach(({ id }) => this.checkChildrenOfParentNode(id, check))
    },
    handleCheckedItem(args) {
      const { checked, itemId } = args
      // Sync with the parent
      this.listOfItems = this.listOfItems.map((item) => {
        if (item.id === itemId) {
          item.checked = checked
        }
        return item
      })

      this.checkChildrenOfParentNode(itemId, checked)

      if (!checked) {
        this.uncheckParentsOfNode(itemId)
      }
    },
    uncheckParentsOfNode(node) {
      const parent = this.listOfItems.find(({ id }) => id === node)?.parent_node
      if (parent && !this.isAsset) {
        const updatedItems = [...this.listOfItems]
        updatedItems.forEach((item) => {
          if (item.id === parent && item.checked) {
            item.checked = false
          }
        })
        this.listOfItems = updatedItems
        // Recrusive
        this.uncheckParentsOfNode(parent)
      }
    },
    emitCheckedItems() {
      if (this.isAsset) {
        const items = [...this.listOfItems].filter(({ checked }) => checked).map(({ id }) => id)
        this.$emit('selector-updated', items)
      }
      else {
        const items = [...this.listOfItems].filter(({ checked, is_metric }) => checked && is_metric).map(({ id }) => id)
        this.$emit('selector-updated', items)
      }
    },
    transformItems(itemDatas) {
      return this.isAsset
        ? [...itemDatas].filter(({ asset_classification }) => asset_classification?.id).map((item) => {
            item.expandedChildren = !item.parent_node
            item.name = item.asset_name
            item.checked = false
            item.disabledSelection = false
            return item
          })
        : [...itemDatas].map((item) => {
            item.expandedChildren = !item.parent_node
            item.disabledSelection = false
            return item
          })
    },

    selectAllItems() {
      // On select all items
      this.onCheckItemClass({ target: { checked: true } }, 'all')
      this.emitCheckedItems()
    },
    deselectAllItems(newStatus = null) {
      // On deselect all items
      this.listOfItems = this.listOfItems.map((item) => {
        item.expandedChildren = newStatus
        item.checked = false
        item.disabledSelection = false
        return item
      })
      this.emitCheckedItems()
    },
    togglePanel() {
      this.isOpen = !this.isOpen
      nextTick(() => {
        if (!this.isOpen) {
          this.emitCheckedItems()
          this.killAllListeners()
        }
      })
    },
    killAllListeners() {
      document.body.removeEventListener('click', this.handleOutsideClick)
      document.body.removeEventListener('keydown', this.handleEscapeKey)
      document.removeEventListener('scroll', this.positionSelectorPanel, true)
      window.removeEventListener('resize', this.positionSelectorPanel, true)
    },
    closeSelectorAndEmitCheckedItemsEvent() {
      this.isOpen = false
      this.emitCheckedItems()
      this.killAllListeners()
    },
    handleOutsideClick(event) {
      if (!this.$refs[`itemsTreeSelector-${this.uid}`] || !this.$refs[`itemsTreePanel-${this.uid}`]) return

      if (!this.$refs[`itemsTreeSelector-${this.uid}`].contains(event.target) && !this.$refs[`itemsTreePanel-${this.uid}`].contains(event.target)) {
        this.closeSelectorAndEmitCheckedItemsEvent()
      }
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape') {
        this.closeSelectorAndEmitCheckedItemsEvent()
      }
    },
    positionSelectorPanel() {
      const itemsSelector = this.$refs[`itemsTreeSelector-${this.uid}`]
      const itemsPanel = this.$refs[`itemsTreePanel-${this.uid}`]

      if (!itemsSelector || !itemsPanel) return

      const { top, right, left } = itemsSelector.getBoundingClientRect()

      // if we didn't use nextTick, the itemsPanel width and height would be 0
      const { width: itemsPanelWidth } = itemsPanel.getBoundingClientRect()
      const { height: itemsSelectorHeight, width: itemsSelectorWidth } = itemsSelector.getBoundingClientRect()
      const { innerWidth } = window

      // Always have the panel below the selector
      this.selectorTop = top + itemsSelectorHeight

      if (left + itemsPanelWidth > innerWidth) {
        this.selectorLeft = innerWidth - itemsPanelWidth - 10
      }
      else {
        this.selectorLeft = right - itemsSelectorWidth
      }

      this.selectorWidth = itemsSelectorWidth
    },
  },
}
</script>
